/**
 * OpenDork app identity for Nostr events and portable exports.
 *
 * Every event we sign carries these tags so clients/relays can scope
 * OpenDork data and so foreign snapshots are rejected on pull.
 */

/** Human / client id (NIP-89 style client tag) */
export const OPENDORK_CLIENT = 'opendork';

/**
 * App-scoped `d` tag on non-addressable events (NIP-98, NIP-42, etc.).
 * Not a replaceable coordinate — filter/identification only.
 */
export const OPENDORK_APP_D_TAG = 'opendork';

/** Addressable kind for encrypted workspace snapshots */
export const OPENDORK_SYNC_KIND = 39987;

/**
 * Addressable `d` for workspace sync (kind 39987).
 * Coordinate: 39987:<pubkey>:opendork-workspace-v1
 */
export const OPENDORK_SYNC_D_TAG = 'opendork-workspace-v1';

export const OPENDORK_ENCRYPTION_NIP44 = 'nip44';

/** Tags identifying any OpenDork-authored event (non-sync). */
export function opendorkAppTags(extra: string[][] = []): string[][] {
  return [
    ['d', OPENDORK_APP_D_TAG],
    ['client', OPENDORK_CLIENT],
    ...extra,
  ];
}

/** Tags for workspace sync snapshots (addressable). */
export function opendorkSyncTags(extra: string[][] = []): string[][] {
  return [
    ['d', OPENDORK_SYNC_D_TAG],
    ['client', OPENDORK_CLIENT],
    ['encryption', OPENDORK_ENCRYPTION_NIP44],
    ['app', OPENDORK_CLIENT],
    ...extra,
  ];
}

/** True if event tags mark this as an OpenDork sync snapshot. */
export function isOpendorkSyncEvent(tags: string[][]): boolean {
  const d = tags.find((t) => t[0] === 'd')?.[1];
  const client = tags.find((t) => t[0] === 'client')?.[1];
  const app = tags.find((t) => t[0] === 'app')?.[1];
  const enc = tags.find((t) => t[0] === 'encryption')?.[1];

  if (d !== OPENDORK_SYNC_D_TAG) return false;
  // Require client or app tag so foreign kind-39987 with same d are rejected
  if (client !== OPENDORK_CLIENT && app !== OPENDORK_CLIENT) return false;
  if (enc && enc !== OPENDORK_ENCRYPTION_NIP44) return false;
  return true;
}
